import { navigateTo, useRoute } from '#imports'
import type { Ref } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

/**
 * When the grade in the URL points towards a disabled one, we need to fallback to the one
 * selected by default (provided by the endpoint).
 *
 * This can happen in a few occasions:
 * - direct access on the pp with an out-of-stock grade (user favorites or history for instance)
 * - wrong grade provided by the pickers endpoint (issue raised on their side that should be fixed, but better safe than sorry)
 * - ...?
 */
export async function useGradeFallback(
  pickers: Ref<GetPickersResponse | undefined>,
  route: RouteLocationNormalized = useRoute(),
) {
  const { grade: gradeInURL } = useUrlParams(route)
  const logger = useLogger()

  if (!pickers.value || !gradeInURL.value) {
    return undefined
  }

  const grades = pickers.value.pickerGroups.find(
    (picker) => picker.id === 'grades',
  )?.items

  const selectedGrade = grades?.find(
    (grade) => grade && grade.trackingValue && grade.selected,
  )

  if (!selectedGrade) {
    return undefined
  }

  if (
    !selectedGrade.available ||
    selectedGrade.parameters?.grade.value !== gradeInURL.value
  ) {
    // Prefer the grade the endpoint selected as the default. If it happens to be
    // unavailable, fall back to the first available grade.
    const targetGrade = selectedGrade.available
      ? selectedGrade
      : grades?.find((grade) => grade.available === true)

    if (targetGrade) {
      logger.info('[PRODUCT] Disabled grade in the URL', {
        owners: ['bot-squad-product-page-front'],
      })

      const link = {
        ...route,
        type: 'internal',
        query: {
          ...route.query,
          l: targetGrade.parameters?.grade.value.toString(),
        },
        replace: true,
      }

      return navigateTo(link)
    }
  }

  return undefined
}
